import { ICellRendererParams } from 'ag-grid-enterprise';
import { formatDateFull, parseLocalDate } from './date.utils';

// ─── Status Badge ───────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<string, { bg: string; fg: string; icon: string }> = {
  Pending:  { bg: 'rgba(219, 216, 64, 0.1)', fg: 'rgba(172, 169, 17, 1)', icon: 'schedule' },
  Approved: { bg: 'rgba(91, 192, 37, 0.1)', fg: 'rgba(91, 192, 37, 1)', icon: 'check_circle' },
  Active:   { bg: 'rgba(91, 192, 37, 0.1)', fg: 'rgba(91, 192, 37, 1)', icon: 'check_circle' },
  Cancelled: { bg: 'rgba(208, 26, 44, 0.1)', fg: 'rgba(208, 26, 44, 1)', icon: 'cancel' },
  Rejected: { bg: 'rgba(208, 26, 44, 0.1)', fg: 'rgba(208, 26, 44, 1)', icon: 'cancel' },
  Declined: { bg: 'rgba(208, 26, 44, 0.1)', fg: 'rgba(208, 26, 44, 1)', icon: 'cancel' },
  Inactive: { bg: 'rgba(208, 26, 44, 0.1)', fg: 'rgba(208, 26, 44, 1)', icon: 'cancel' },
};

export function statusBadgeRenderer(params: ICellRendererParams): string {
  const rawStatus = params.value as string;
  const statusNormal = (rawStatus ?? '').trim().toLowerCase();
  const status = statusNormal === 'rejected' ? 'Declined' : rawStatus;
  const config = STATUS_CONFIG[status] ?? STATUS_CONFIG['Pending'];
  return `
    <div style="display:inline-flex;align-items:center;gap:4px;padding:4px 12px;
                background:${config.bg};color:${config.fg};border-radius:4px;
                font-size:12px;font-weight:600;white-space:nowrap">
      <span class="material-icons" style="font-size:14px;line-height:14px">${config.icon}</span>
      <span style="line-height:14px">${status}</span>
    </div>`;
}

const AVATAR_COLORS = [
  '#2469A0', '#7B61FF', '#E85D75', '#F59E0B', '#10B981',
  '#6366F1', '#EC4899', '#14B8A6', '#F97316', '#8B5CF6',
  '#EF4444', '#3B82F6', '#22C55E', '#A855F7', '#06B6D4',
];

function hashStringToColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function leaveTypeRenderer(params: ICellRendererParams): string {
  const name = params.value || 'UNK';
  const sc = params.data?.shortCode ?? name.substring(0, 2).toUpperCase();
  const color = (params.data?.color && params.data.color.trim()) ? params.data.color : hashStringToColor(name);
  return `
    <div style="display:flex;align-items:center;gap:10px;height:100%">
      <div style="width:28px;height:28px;border-radius:50%;background:${color};color:#fff;
                  display:flex;align-items:center;justify-content:center;
                  font-size:11px;font-weight:700;flex-shrink:0">${sc}</div>
      <span style="font-size:13px;color:#1e293b">${params.value}</span>
    </div>`;
}

const NON_CANCELLABLE_STATUSES = new Set(['Rejected', 'Declined', 'Cancelled', 'Inactive']);

function getStartDateForCancellation(data: any): Date | null {
  const raw = data?.startDateRaw ?? data?.dateRaw ?? data?.from;
  if (!raw) return null;
  const d = parseLocalDate(raw);
  return isNaN(d.getTime()) ? null : d;
}

function shouldDisableCancelByDate(data: any): boolean {
  // Hourly: compare actual start datetime vs now (minute precision)
  if (data?.isHourly) {
    const startIso = data?.startIsoRaw as string | undefined;
    const startDt = startIso ? parseLocalDate(startIso) : null;
    return startDt && !isNaN(startDt.getTime()) ? startDt <= new Date() : true;
  }
  // Leave: compare date only
  const startDate = getStartDateForCancellation(data);
  if (!startDate) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  startDate.setHours(0, 0, 0, 0);
  return startDate <= today;
}

/** Returns true if the request's start date/time is in the past */
function isRequestDatePast(data: any): boolean {
  if (data?.isHourly) {
    const startIso = data?.startIsoRaw as string | undefined;
    const startDt = startIso ? parseLocalDate(startIso) : null;
    return startDt && !isNaN(startDt.getTime()) ? startDt <= new Date() : false;
  }
  const raw = data?.startDateRaw ?? data?.from;
  if (!raw) return false;
  const d = parseLocalDate(raw);
  if (isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return d < today;
}

export function cancelButtonRenderer(params: ICellRendererParams): string {
  const status     = params.data?.status as string;
  const id         = params.data?.id ?? '';

  // Allow cancelling Approved leaves if today is before the leave start date
  let isDisabled = NON_CANCELLABLE_STATUSES.has(status);
  if (status === 'Approved') {
    const startDateRaw = params.data?.startDateRaw ?? params.data?.from;
    if (startDateRaw) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDate = parseLocalDate(startDateRaw);
      isDisabled = !isNaN(startDate.getTime()) && startDate <= today;
    } else {
      isDisabled = true;
    }
  }

  const borderColor = '#BDBDBD';
  const textColor   = isDisabled ? '#BDBDBD' : '#D32F2F';
  const cursor      = isDisabled ? 'not-allowed' : 'pointer';
  return `
    <button ${isDisabled ? 'disabled' : ''}
            data-action="cancel" data-id="${id}"
            style="background:#fff;border:0.1px solid ${borderColor};color:${textColor};
                   padding:4px 8px;border-radius:6px;font-size:12px;display:inline-flex;
                   align-items:center;gap:6px;font-weight:600;cursor:${cursor}">
      <span class="material-icons" style="font-size:14px;line-height:14px">event_busy</span>
      <span style="line-height:14px">Cancel</span>
    </button>`;
}

export function editDeleteActionRenderer(params: ICellRendererParams): string {
  const id = params.data?.id;
  return `
    <div style="display:flex;align-items:center;gap:10px;height:100%">
      <button data-action="edit" data-id="${id}"
              style="background:none;border:none;cursor:pointer;padding:0" title="Edit">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 21V16.75L16.2 3.575C16.4 3.39167 16.621 3.25 16.863 3.15C17.105 3.05 17.359 3 17.625 3C17.891 3 18.1493 3.05 18.4 3.15C18.6507 3.25 18.8673 3.4 19.05 3.6L20.425 5C20.625 5.18333 20.771 5.4 20.863 5.65C20.955 5.9 21.0007 6.15 21 6.4C21 6.66667 20.9543 6.921 20.863 7.163C20.7717 7.405 20.6257 7.62567 20.425 7.825L7.25 21H3ZM17.6 7.8L19 6.4L17.6 5L16.2 6.4L17.6 7.8Z" fill="#18669F"/>
        </svg>
      </button>
      <button data-action="delete" data-id="${id}"
              style="background:none;border:none;cursor:pointer;padding:0" title="Delete">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.616 20.0005C7.15533 20.0005 6.771 19.8465 6.463 19.5385C6.155 19.2305 6.00067 18.8458 6 18.3845V6.00047H5V5.00047H9V4.23047H15V5.00047H19V6.00047H18V18.3855C18 18.8455 17.846 19.2298 17.538 19.5385C17.23 19.8471 16.8453 20.0011 16.384 20.0005H7.616ZM9.808 17.0005H10.808V8.00047H9.808V17.0005ZM13.192 17.0005H14.192V8.00047H13.192V17.0005Z" fill="#D01A2C"/>
        </svg>
      </button>
    </div>`;
}

export function docActionRenderer(params: ICellRendererParams): string {
  const id = params.data?.id;
  return `
    <div style="display:flex;align-items:center;gap:4px;height:100%">
      <button class="lp-doc-action" data-action="preview" data-id="${id}"
              style="background:none;border:none;cursor:pointer;padding:4px;border-radius:4px;display:flex;align-items:center;justify-content:center;transition:background 0.15s" title="Preview">
        <span style="font-size:18px;color:#2469A0;line-height:1;display:flex;font-family:'Material Icons'">visibility</span>
      </button>
      <button class="lp-doc-action" data-action="edit" data-id="${id}"
              style="background:none;border:none;cursor:pointer;padding:4px;border-radius:4px;display:flex;align-items:center;justify-content:center;transition:background 0.15s" title="Edit">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 21V16.75L16.2 3.575C16.4 3.39167 16.621 3.25 16.863 3.15C17.105 3.05 17.359 3 17.625 3C17.891 3 18.1493 3.05 18.4 3.15C18.6507 3.25 18.8673 3.4 19.05 3.6L20.425 5C20.625 5.18333 20.771 5.4 20.863 5.65C20.955 5.9 21.0007 6.15 21 6.4C21 6.66667 20.9543 6.921 20.863 7.163C20.7717 7.405 20.6257 7.62567 20.425 7.825L7.25 21H3ZM17.6 7.8L19 6.4L17.6 5L16.2 6.4L17.6 7.8Z" fill="#18669F"/>
        </svg>
      </button>
      <button class="lp-doc-action" data-action="download" data-id="${id}"
              style="background:none;border:none;cursor:pointer;padding:4px;border-radius:4px;display:flex;align-items:center;justify-content:center;transition:background 0.15s" title="Download">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14.0156 3.58203L14.1326 3.58903C14.3558 3.61536 14.5637 3.71611 14.7226 3.87504C14.8815 4.03397 14.9823 4.24181 15.0086 4.46503L15.0156 4.58203V8.58203L15.0206 8.73203C15.0564 9.2082 15.2614 9.65586 15.5985 9.99406C15.9356 10.3323 16.3826 10.5387 16.8586 10.576L17.0156 10.582H21.0156L21.1326 10.589C21.3558 10.6154 21.5637 10.7161 21.7226 10.875C21.8815 11.034 21.9823 11.2418 22.0086 11.465L22.0156 11.582V20.582C22.0157 21.3472 21.7233 22.0835 21.1983 22.6403C20.6734 23.197 19.9555 23.5321 19.1916 23.577L19.0156 23.582H9.01562C8.25041 23.5821 7.51411 23.2897 6.95736 22.7647C6.40062 22.2398 6.06552 21.5219 6.02063 20.758L6.01563 20.582V6.58203C6.01558 5.81682 6.30795 5.08052 6.83291 4.52377C7.35787 3.96702 8.07573 3.63192 8.83962 3.58703L9.01562 3.58203H14.0156ZM14.0156 11.582C13.7504 11.582 13.4961 11.6874 13.3085 11.8749C13.121 12.0625 13.0156 12.3168 13.0156 12.582V16.167L12.2226 15.375C12.0504 15.2029 11.8213 15.0994 11.5783 15.0841C11.3353 15.0689 11.095 15.1428 10.9026 15.292L10.8086 15.375C10.6212 15.5626 10.5158 15.8169 10.5158 16.082C10.5158 16.3472 10.6212 16.6015 10.8086 16.789L13.3086 19.289L13.3526 19.331L13.4206 19.386L13.5306 19.457L13.6446 19.511L13.7496 19.546L13.8996 19.576L14.0156 19.582L14.1326 19.575L14.2496 19.555L14.3576 19.522L14.4386 19.488L14.5366 19.436L14.6286 19.372L14.7226 19.289L17.2226 16.789C17.4101 16.6015 17.5154 16.3472 17.5154 16.082C17.5154 15.8169 17.4101 15.5626 17.2226 15.375L17.1286 15.292C16.9362 15.1428 16.696 15.0689 16.4529 15.0841C16.2099 15.0994 15.9808 15.2029 15.8086 15.375L15.0156 16.166V12.582C15.0156 12.3371 14.9257 12.1007 14.7629 11.9177C14.6002 11.7346 14.3759 11.6177 14.1326 11.589L14.0156 11.582ZM17.0146 4.58103L21.0156 8.58203H17.0156L17.0146 4.58103Z" fill="#18669F"/>
        </svg>
      </button>
      <button class="lp-doc-action" data-action="delete" data-id="${id}"
              style="background:none;border:none;cursor:pointer;padding:4px;border-radius:4px;display:flex;align-items:center;justify-content:center;transition:background 0.15s" title="Delete">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.616 20.0005C7.15533 20.0005 6.771 19.8465 6.463 19.5385C6.155 19.2305 6.00067 18.8458 6 18.3845V6.00047H5V5.00047H9V4.23047H15V5.00047H19V6.00047H18V18.3855C18 18.8455 17.846 19.2298 17.538 19.5385C17.23 19.8471 16.8453 20.0011 16.384 20.0005H7.616ZM9.808 17.0005H10.808V8.00047H9.808V17.0005ZM13.192 17.0005H14.192V8.00047H13.192V17.0005Z" fill="#D01A2C"/>
        </svg>
      </button>
    </div>`;
}

export function docActionEmployeeRenderer(params: ICellRendererParams): string {
  const id = params.data?.id;
  return `
    <div style="display:flex;align-items:center;gap:4px;height:100%">
      <button class="lp-doc-action" data-action="preview" data-id="${id}"
              style="background:none;border:none;cursor:pointer;padding:4px;border-radius:4px;display:flex;align-items:center;justify-content:center;transition:background 0.15s" title="Preview">
        <span style="font-size:18px;color:#2469A0;line-height:1;display:flex;font-family:'Material Icons'">visibility</span>
      </button>
      <button class="lp-doc-action" data-action="download" data-id="${id}"
              style="background:none;border:none;cursor:pointer;padding:4px;border-radius:4px;display:flex;align-items:center;justify-content:center;transition:background 0.15s" title="Download">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14.0156 3.58203L14.1326 3.58903C14.3558 3.61536 14.5637 3.71611 14.7226 3.87504C14.8815 4.03397 14.9823 4.24181 15.0086 4.46503L15.0156 4.58203V8.58203L15.0206 8.73203C15.0564 9.2082 15.2614 9.65586 15.5985 9.99406C15.9356 10.3323 16.3826 10.5387 16.8586 10.576L17.0156 10.582H21.0156L21.1326 10.589C21.3558 10.6154 21.5637 10.7161 21.7226 10.875C21.8815 11.034 21.9823 11.2418 22.0086 11.465L22.0156 11.582V20.582C22.0157 21.3472 21.7233 22.0835 21.1983 22.6403C20.6734 23.197 19.9555 23.5321 19.1916 23.577L19.0156 23.582H9.01562C8.25041 23.5821 7.51411 23.2897 6.95736 22.7647C6.40062 22.2398 6.06552 21.5219 6.02063 20.758L6.01563 20.582V6.58203C6.01558 5.81682 6.30795 5.08052 6.83291 4.52377C7.35787 3.96702 8.07573 3.63192 8.83962 3.58703L9.01562 3.58203H14.0156ZM14.0156 11.582C13.7504 11.582 13.4961 11.6874 13.3085 11.8749C13.121 12.0625 13.0156 12.3168 13.0156 12.582V16.167L12.2226 15.375C12.0504 15.2029 11.8213 15.0994 11.5783 15.0841C11.3353 15.0689 11.095 15.1428 10.9026 15.292L10.8086 15.375C10.6212 15.5626 10.5158 15.8169 10.5158 16.082C10.5158 16.3472 10.6212 16.6015 10.8086 16.789L13.3086 19.289L13.3526 19.331L13.4206 19.386L13.5306 19.457L13.6446 19.511L13.7496 19.546L13.8996 19.576L14.0156 19.582L14.1326 19.575L14.2496 19.555L14.3576 19.522L14.4386 19.488L14.5366 19.436L14.6286 19.372L14.7226 19.289L17.2226 16.789C17.4101 16.6015 17.5154 16.3472 17.5154 16.082C17.5154 15.8169 17.4101 15.5626 17.2226 15.375L17.1286 15.292C16.9362 15.1428 16.696 15.0689 16.4529 15.0841C16.2099 15.0994 15.9808 15.2029 15.8086 15.375L15.0156 16.166V12.582C15.0156 12.3371 14.9257 12.1007 14.7629 11.9177C14.6002 11.7346 14.3759 11.6177 14.1326 11.589L14.0156 11.582ZM17.0146 4.58103L21.0156 8.58203H17.0156L17.0146 4.58103Z" fill="#18669F"/>
        </svg>
      </button>
    </div>`;
}

export function daysRenderer(params: ICellRendererParams): string {
  return `<span style="font-size:13px">${params.value} Days</span>`;
}

export function dateRenderer(params: ICellRendererParams): string {
  if (!params.value) return '–';
  return formatDateFull(params.value);
}

export function requestButtonRenderer(params?: ICellRendererParams): string {
  const id   = params?.data?.leaveTypeId ?? '';
  const name = params?.data?.leaveTypeName ?? '';
  return `
    <button data-action="request" data-id="${id}" data-name="${name}"
            style="padding:2px 8px;border-radius:4px;border:1px solid #2469A0;
                   background:#2469A0;color:#fff;font-size:11px;font-weight:600;
                   line-height:16px;white-space:nowrap">+Request Leave</button>`;
}

export function carryForwardWithInfoRenderer(params?: ICellRendererParams): string {
  const value = params?.value ?? 0;

  const tooltip = 'Leaves Carried Forward from Previous Year.';

  const onEnter = `
    (function(icon){
      var tip = document.createElement('div');
      tip.className = 'ers-tooltip ers-tooltip-top';
      var span = document.createElement('span');
      span.className = 'ers-tooltip-text';
      span.textContent = '${tooltip}';
      tip.appendChild(span);
      var arrow = document.createElement('div');
      arrow.className = 'ers-tooltip-arrow';
      tip.appendChild(arrow);
      document.body.appendChild(tip);
      icon._ersTip = tip;
      var r = icon.getBoundingClientRect();
      var tr = tip.getBoundingClientRect();
      tip.style.top  = (r.top - tr.height - 8 + window.scrollY) + 'px';
      tip.style.left = (r.left + (r.width - tr.width)/2 + window.scrollX) + 'px';
      requestAnimationFrame(function(){ tip.classList.add('ers-tooltip-visible'); });
    })(this)`.replace(/\n\s*/g, ' ');

  const onLeave = `
    (function(icon){
      if(!icon._ersTip) return;
      icon._ersTip.classList.remove('ers-tooltip-visible');
      setTimeout(function(){ if(icon._ersTip && icon._ersTip.parentNode){ icon._ersTip.parentNode.removeChild(icon._ersTip); } icon._ersTip=null; }, 150);
    })(this)`.replace(/\n\s*/g, ' ');

  return `
    <span style="display:inline-flex;align-items:center;gap:4px">
      <span>${value}</span>
      <span class="material-icons"
            onmouseenter="${onEnter}"
            onmouseleave="${onLeave}"
            style="font-size:14px;color:#888;cursor:default;vertical-align:middle">info</span>
    </span>`;
}

export function alWithAlertRenderer(params: ICellRendererParams): string {
  if (!params.data) return '';
  const icon = params.data.alertIcon
    ? '<span class="material-icons alert-icon">error</span>'
    : '';
  return `<span>${params.data.al}</span>${icon}`;
}

export function managerActionRenderer(params: ICellRendererParams): string {
  const id          = params.data?.id ?? '';
  const status      = (params.data?.status ?? '') as string;
  const statusNormal = status.trim().toLowerCase();

  if (statusNormal === 'pending') {
    return `
      <div style="display:flex;align-items:center;gap:6px;height:100%">
        <button data-action="approve" data-id="${id}"
                style="display:inline-flex;align-items:center;gap:4px;padding:4px 10px;
                       background:#5BC025;color:#fff;border:none;border-radius:6px;
                       font-size:12px;font-weight:600;cursor:pointer;white-space:nowrap">
          <span class="material-icons" style="font-size:14px;line-height:14px">check_circle</span>
          <span style="line-height:14px">Approve</span>
        </button>
        <button data-action="decline" data-id="${id}"
                style="display:inline-flex;align-items:center;gap:4px;padding:4px 10px;
                       background:#fff;color:#D01A2C;border:1.5px solid #D01A2C;border-radius:6px;
                       font-size:12px;font-weight:600;cursor:pointer;white-space:nowrap">
          <span class="material-icons" style="font-size:14px;line-height:14px">cancel</span>
          <span style="line-height:14px">Decline</span>
        </button>
      </div>`;
  }

  if (statusNormal === 'approved') {
    const isDisabled = shouldDisableCancelByDate(params.data);
    const cancelTextColor = isDisabled ? '#BDBDBD' : '#D32F2F';
    const cancelCursor = isDisabled ? 'not-allowed' : 'pointer';
    return `
      <div style="display:flex;align-items:center;gap:8px;height:100%">
        <div style="display:inline-flex;align-items:center;gap:4px;padding:4px 12px;
                    background:rgba(91,192,37,0.1);color:rgba(91,192,37,1);border-radius:4px;
                    font-size:12px;font-weight:600;white-space:nowrap">
          <span class="material-icons" style="font-size:14px;line-height:14px">check_circle</span>
          <span style="line-height:14px">Approved</span>
        </div>
        <button ${isDisabled ? 'disabled' : ''} data-action="cancel" data-id="${id}"
                style="background:#fff;border:0.1px solid #BDBDBD;color:${cancelTextColor};
                       padding:4px 8px;border-radius:6px;font-size:12px;display:inline-flex;
                       align-items:center;gap:4px;font-weight:600;cursor:${cancelCursor};white-space:nowrap">
          <span class="material-icons" style="font-size:14px;line-height:14px">event_busy</span>
          <span style="line-height:14px">Cancel</span>
        </button>
      </div>`;
  }

  // 'rejected' from backend → display as 'Declined'
  const displayStatus = statusNormal === 'rejected' ? 'Declined' : status;
  const configKey     = statusNormal === 'rejected' ? 'Declined' : status;
  const config        = STATUS_CONFIG[configKey] ?? STATUS_CONFIG['Declined'];
  return `
    <div style="display:inline-flex;align-items:center;gap:4px;padding:4px 12px;
                background:${config.bg};color:${config.fg};border-radius:4px;
                font-size:12px;font-weight:600;white-space:nowrap">
      <span class="material-icons" style="font-size:14px;line-height:14px">${config.icon}</span>
      <span style="line-height:14px">${displayStatus}</span>
    </div>`;
}

/**
 * Combined action renderer for hourly permissions in manager/HR view.
 * Pending → Approve + Decline buttons
 * Cancelled → disabled "Cancelled Request" button
 * Other (Approved, etc.) → "Cancel Request" button
 */
export function hourlyManagerActionRenderer(params: ICellRendererParams): string {
  const id            = params.data?.id ?? '';
  const status        = (params.data?.status ?? '') as string;
  const statusNormal  = status.trim().toLowerCase();

  if (statusNormal === 'pending') {
    return `
      <div style="display:flex;align-items:center;gap:6px;height:100%">
        <button data-action="approve" data-id="${id}"
                style="display:inline-flex;align-items:center;gap:4px;padding:4px 10px;
                       background:#5BC025;color:#fff;border:none;border-radius:6px;
                       font-size:12px;font-weight:600;cursor:pointer;white-space:nowrap">
          <span class="material-icons" style="font-size:14px;line-height:14px">check_circle</span>
          <span style="line-height:14px">Approve</span>
        </button>
        <button data-action="decline" data-id="${id}"
                style="display:inline-flex;align-items:center;gap:4px;padding:4px 10px;
                       background:#fff;color:#D01A2C;border:1.5px solid #D01A2C;border-radius:6px;
                       font-size:12px;font-weight:600;cursor:pointer;white-space:nowrap">
          <span class="material-icons" style="font-size:14px;line-height:14px">cancel</span>
          <span style="line-height:14px">Decline</span>
        </button>
      </div>`;
  }

  if (statusNormal === 'approved') {
    const isDisabled = shouldDisableCancelByDate(params.data);
    const cancelTextColor = isDisabled ? '#BDBDBD' : '#D32F2F';
    const cancelCursor = isDisabled ? 'not-allowed' : 'pointer';
    return `
      <div style="display:flex;align-items:center;gap:8px;height:100%">
        <div style="display:inline-flex;align-items:center;gap:4px;padding:4px 12px;
                    background:rgba(91,192,37,0.1);color:rgba(91,192,37,1);border-radius:4px;
                    font-size:12px;font-weight:600;white-space:nowrap">
          <span class="material-icons" style="font-size:14px;line-height:14px">check_circle</span>
          <span style="line-height:14px">Approved</span>
        </div>
        <button ${isDisabled ? 'disabled' : ''} data-action="cancel" data-id="${id}"
                style="background:#fff;border:0.1px solid #BDBDBD;color:${cancelTextColor};
                       padding:4px 8px;border-radius:6px;font-size:12px;display:inline-flex;
                       align-items:center;gap:4px;font-weight:600;cursor:${cancelCursor};white-space:nowrap">
          <span class="material-icons" style="font-size:14px;line-height:14px">event_busy</span>
          <span style="line-height:14px">Cancel</span>
        </button>
      </div>`;
  }

  // 'rejected' from backend → display as 'Declined'
  const displayStatus = statusNormal === 'rejected' ? 'Declined' : status;
  const configKey     = statusNormal === 'rejected' ? 'Declined' : status;
  const config        = STATUS_CONFIG[configKey] ?? STATUS_CONFIG['Declined'];
  return `
    <div style="display:inline-flex;align-items:center;gap:4px;padding:4px 12px;
                background:${config.bg};color:${config.fg};border-radius:4px;
                font-size:12px;font-weight:600;white-space:nowrap">
      <span class="material-icons" style="font-size:14px;line-height:14px">${config.icon}</span>
      <span style="line-height:14px">${displayStatus}</span>
    </div>`;
}

/**
 * Cancel button renderer that shows "Cancelled Request" text for cancelled status.
 */
export function cancelButtonWithStatusRenderer(params: ICellRendererParams): string {
  const status     = params.data?.status as string;
  const id         = params.data?.id ?? '';

  // Show status badge for terminal statuses (Rejected, Declined, Cancelled, Inactive)
  if (NON_CANCELLABLE_STATUSES.has(status)) {
    return statusBadgeRenderer({ ...params, value: status } as ICellRendererParams);
  }

  // Pending requests can always be cancelled by the employee
  if (status === 'Pending') {
    return `
    <button data-action="cancel" data-id="${id}"
            style="background:#fff;border:0.1px solid #BDBDBD;color:#D32F2F;
                   padding:4px 8px;border-radius:6px;font-size:12px;display:inline-flex;
                   align-items:center;gap:6px;font-weight:600;cursor:pointer">
      <span class="material-icons" style="font-size:14px;line-height:14px">event_busy</span>
      <span style="line-height:14px">Cancel</span>
    </button>`;
  }

  // Approved: disable cancel if start date has passed
  const isHourly     = !!params.data?.isHourly;
  const startIsoRaw  = params.data?.startIsoRaw as string | undefined;
  const startDateRaw = params.data?.startDateRaw as string | undefined;
  let isDisabled: boolean;
  if (isHourly) {
    const startDt = startIsoRaw ? parseLocalDate(startIsoRaw) : null;
    isDisabled = startDt && !isNaN(startDt.getTime()) ? startDt <= new Date() : true;
  } else if (startDateRaw) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = parseLocalDate(startDateRaw);
    startDate.setHours(0, 0, 0, 0);
    isDisabled = !isNaN(startDate.getTime()) && startDate <= today;
  } else {
    isDisabled = true;
  }

  const textColor  = isDisabled ? '#BDBDBD' : '#D32F2F';
  const cursor     = isDisabled ? 'not-allowed' : 'pointer';
  return `
    <button ${isDisabled ? 'disabled' : ''}
            data-action="cancel" data-id="${id}"
            style="background:#fff;border:0.1px solid #BDBDBD;color:${textColor};
                   padding:4px 8px;border-radius:6px;font-size:12px;display:inline-flex;
                   align-items:center;gap:6px;font-weight:600;cursor:${cursor}">
      <span class="material-icons" style="font-size:14px;line-height:14px">event_busy</span>
      <span style="line-height:14px">Cancel</span>
    </button>`;
}

export function dashIfNull(params: ICellRendererParams): string {
  return params.value != null ? `${params.value}` : '–';
}

export function docNameRenderer(params: ICellRendererParams): string {
  return `
    <div style="display:flex;align-items:center;gap:8px;height:100%">
      <span class="material-icons" style="font-size:20px;color:#757575">description</span>
      <span style="font-size:13px">${params.value}</span>
    </div>`;
}
